<template>
  <div>
    <CustomDropdown
      ref="dropdownRef"
      max-height="400px"
      gap="5px"
      :position="position"
      dropdownCSS="p-0"
      @close="handleDropdownClose"
      @triggerClick="handleDropdownTriggerClick"
      :disabled="disabled"
      :width="getDropdownContainerWidth"
      :relative-to-viewport="relativeToViewport"
    >
      <template #trigger>
        <div
          ref="triggerElement"
          :class="[
            'input_medium',
            'flex items-center justify-between cursor-pointer relative',
            customClass,
            { input_error: hasError },
          ]"
          :style="getComputedStyles"
        >
          <div class="flex items-center flex-wrap gap-1 flex-1 min-w-0">
            <template v-if="multiple">
              <template v-if="getCurrentTabSelectedValues.length === 0">
                <span class="secondary_color">{{ placeholder }}</span>
              </template>
              <template v-else-if="isAllUsersSelected">
                <span class="text_color">All Users</span>
              </template>
              <template v-else>
                <div
                  v-for="value in getCurrentTabSelectedValues"
                  :key="value"
                  class="selected_item_tag flex items-center gap-1 px-2 py-1 rounded-full text-xs"
                >
                  <template v-if="getOptionByValue(value)">
                    <ProfileIcon
                      v-if="getOptionByValue(value).userData"
                      :data="getOptionByValue(value).userData"
                      :size="20"
                      :font="8"
                    />
                  </template>
                  <span>{{ getDisplayLabel(value) }}</span>
                  <div
                    v-if="!getOptionByValue(value)?.disabled"
                    @click.stop="removeItem(value)"
                    @mousedown.stop
                    class="cursor-pointer hover:opacity-70"
                  >
                    <ThemeImage
                      :icon="imageNames.CLOSE_GREY_FILLED"
                      alt="remove"
                      class="w-3 h-3"
                    />
                  </div>
                </div>
              </template>
            </template>

            <template v-else>
              <span
                v-if="
                  !selectedValues ||
                  (tabs && tabs.length > 0 && getCurrentTabSelectedValues.length === 0)
                "
                class="secondary_color"
              >
                {{ placeholder }}
              </span>
              <span v-else class="text_color">
                {{
                  tabs && tabs.length > 0
                    ? getDisplayLabel(getCurrentTabSelectedValues[0])
                    : getDisplayLabel(selectedValues)
                }}
              </span>
            </template>
          </div>

          <div class="flex items-center ml-2">
            <div
              class="w-4 h-4 flex items-center justify-center cursor-pointer trigger_icon_container"
              @click.stop="handleTriggerIconClick"
            >
              <ThemeImage
                :icon="imageNames.CLOSE_GREY_FILLED"
                alt="clear"
                class="w-4 h-4 trigger_clear_icon"
                v-if="showClear && !multiple && selectedValues"
              />
              <ThemeImage
                :icon="imageNames.BOTTOM_ARROW"
                alt="arrow"
                class="w-4 h-4 trigger_arrow_icon"
              />
            </div>
          </div>
        </div>
      </template>

      <template #default>
        <div
          class="flex flex-col"
          :class="dropdownClass"
          :style="getDropdownStyles"
        >
          <div class="flex flex-col max-h-[300px]">
            <div
              v-if="tabs && tabs.length > 0"
              class="flex flex-col gap-2 px-2 pt-2"
            >
              <div class="flex flex-row gap-1">
                <div
                  v-for="(tab, index) in tabs"
                  :key="`tab_${index}`"
                  :class="[
                    'list_category px-2 py-1 font-semibold rounded-lg cursor-pointer text-sm transition-all duration-300 ease-in-out flex items-center gap-1.5',
                    { active: activeTabIndex === index },
                  ]"
                  @click="setActiveTab(index)"
                >
                  <span>{{ tab }}</span>
                  <div v-if="getTabCount(index) > 0" class="tab_count_badge">
                    {{ getTabCount(index) }}
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="!hideHeader"
              class="flex flex-col py-3 px-2"
              :class="{ border_bottom: currentFilteredOptions?.length }"
            >
              <span
                v-if="!tabs || tabs.length === 0"
                class="secondary_color font-semibold text-xs leading-[17px] px-3 pb-2"
              >
                {{ getCurrentSearchTitle.toUpperCase() }}
              </span>
              <CustomInput
                format="text"
                v-model="searchQuery"
                :placeholder="searchPlaceholder"
                type="primary"
                :icon="imageNames.SEARCH_VARIANT1"
                alt="search_icon"
                auto-focus
              />
            </div>

            <div
              v-if="currentFilteredOptions?.length"
              class="flex flex-col px-2 py-2 overflow-y-scroll custom_scrollbar max-h-[200px] options-scrollable-container"
            >
              <div
                v-if="props.showSelectAll && isCurrentTabMultiSelect && !searchQuery"
                :class="[
                  'dropdown_item flex-shrink-0 flex items-center flex-row gap-2 px-3 py-2 rounded-lg',
                  {
                    'cursor-pointer': !isSelectAllDisabled,
                    'cursor-not-allowed opacity-50': isSelectAllDisabled,
                  },
                ]"
                @click="!isSelectAllDisabled && handleSelectAll()"
              >
                <div class="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <template v-if="isCurrentTabMultiSelect">
                    <a-checkbox
                      :checked="isAllSelected"
                      :disabled="isSelectAllDisabled"
                    />
                  </template>
                  <template v-else>
                    <ThemeImage
                      v-if="isAllSelected"
                      :icon="imageNames.TICK_GREY"
                      alt="selected"
                      class="w-4 h-4"
                    />
                  </template>
                </div>
                <div class="flex items-center flex-1 justify-between">
                  <div class="flex items-center gap-2">
                    <span class="text_color text-sm font-normal leading-[17px]">
                      Select All
                    </span>
                  </div>
                </div>
              </div>
              
              <div
                v-for="(option, index) in currentFilteredOptions"
                :key="`${getOptionValue(option)}_option_${index}`"
                :class="[
                  'dropdown_item flex-shrink-0 flex items-center flex-row gap-2 px-3 py-2 rounded-lg',
                  {
                    'cursor-pointer': !option.disabled,
                    'cursor-not-allowed opacity-50': option.disabled,
                    dropdown_item_selected: isOptionSelected(
                      getOptionValue(option),
                    ),
                  },
                ]"
                @click="
                  !option.disabled && toggleSelection(getOptionValue(option))
                "
              >
                <div
                  class="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5"
                >
                  <template v-if="isCurrentTabMultiSelect">
                    <a-checkbox
                      :checked="isOptionSelected(getOptionValue(option))"
                      :disabled="option.disabled"
                      @change="
                        (e) =>
                          !option.disabled &&
                          handleCheckboxChange(e, getOptionValue(option))
                      "
                      @click.stop
                    />
                  </template>
                  <template v-else>
                    <ThemeImage
                      v-if="isOptionSelected(getOptionValue(option))"
                      :icon="imageNames.TICK_GREY"
                      alt="selected"
                      class="w-4 h-4"
                    />
                  </template>
                </div>
                <div class="flex items-center flex-1 justify-between">
                  <div class="flex items-center gap-2">
                    <ProfileIcon
                      v-if="option.userData"
                      :data="option.userData"
                      :size="24"
                      :font="10"
                    />
                    <span class="text_color text-sm font-normal leading-[17px]">
                      {{ getOptionLabel(option) }}
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <a-tooltip
                      v-if="getWarningMessage(option)"
                      :title="getWarningMessage(option)"
                      placement="top"
                    >
                      <div class="warning_icon_container">
                        <ThemeImage
                          :icon="imageNames.WARNING"
                          alt="warning"
                          class="w-4 h-4 flex-shrink-0"
                        />
                      </div>
                    </a-tooltip>
                    <ThemeImage
                      v-if="shouldAllowRemove(option)"
                      :icon="imageNames.DELETE_GREY"
                      alt="remove"
                      class="w-4 h-4 flex-shrink-0 cursor-pointer hover:opacity-70"
                      @click.stop="handleRemoveOption(option)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              v-else
              class="flex flex-col items-center justify-center py-6 px-4"
            >
              <ThemeImage
                :icon="emptyStateIcon"
                alt="empty_icon"
                :class="emptyStateIconSize"
              />
              <span class="text-sm secondary_color text-center mt-2">
                {{ emptyStateText }}
              </span>
            </div>

            <div v-if="ctaText" class="flex flex-col p-2 border_top">
              <div
                class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg cursor-pointer"
                @click="handleCTA"
              >
                <ThemeImage
                  :icon="imageNames.PLUS"
                  alt="add"
                  class="w-4 h-4 flex-shrink-0"
                />
                {{ ctaText }}
              </div>
            </div>
          </div>
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import {computed, nextTick, onMounted, ref, watch} from "vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import {imageNames} from "@/src/constants/imageConstants.js";
import {stepTypeConstants, WARNING_MESSAGES,} from "@/src/constants/sequenceConstants.js";

const hasValidOptions = (options) => {
  return (
    options?.length &&
    options.some((opt) => (Array.isArray(opt) ? opt.length > 0 : true))
  );
};

const normalizeToArray = (value) => {
  return Array.isArray(value) ? value : [value];
};

const hasSelectionChanges = (newSelections, currentSelections) => {
  if (newSelections.length !== currentSelections.length) return true;

  return !newSelections.every((tabSelection, index) => {
    const currentTabSelection = currentSelections[index] || [];
    return (
      tabSelection.length === currentTabSelection.length &&
      tabSelection.every(
        (value, valueIndex) => value === currentTabSelection[valueIndex],
      )
    );
  });
};

const props = defineProps({
  modelValue: {
    type: [Array, String, Number, null],
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  options: {
    type: Array,
    default: () => [],
  },
  tabs: {
    type: Array,
    default: () => [],
  },
  tabSelectionModes: {
    type: Array,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "Select options",
  },
  width: {
    type: String,
    default: "100%",
  },
  minWidth: {
    type: String,
    default: "200px",
  },
  dropdownWidth: {
    type: String,
    default: "320px",
  },
  dropdownClass: {
    type: String,
    default: "",
  },
  matchTriggerWidth: {
    type: Boolean,
    default: false,
  },
  searchTitle: {
    type: String,
    default: "Choose options",
  },
  searchPlaceholder: {
    type: String,
    default: "Search here",
  },
  ctaText: {
    type: String,
    default: "",
  },
  emptyStateText: {
    type: String,
    default: "No results found!",
  },
  emptyStateIcon: {
    type: String,
    default: () => imageNames.EMPTY_SCREEN_GREY,
  },
  emptyStateIconSize: {
    type: String,
    default: "size-16",
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  customClass: {
    type: String,
    default: "",
  },
  showWarnings: {
    type: Boolean,
    default: false,
  },
  warningConfig: {
    type: Object,
    default: () => ({
      emailConnectedKey: "email_connected",
      linkedinConnectedKey: "linkedin_connected",
      messages: {
        emailOnly: WARNING_MESSAGES.USER.EMAIL_ONLY_CONNECTED,
        linkedinOnly: WARNING_MESSAGES.USER.LINKEDIN_ONLY_CONNECTED,
        both: WARNING_MESSAGES.USER.BOTH_DISCONNECTED,
        emailDisconnected: WARNING_MESSAGES.USER.EMAIL_DISCONNECTED,
        linkedinDisconnected: WARNING_MESSAGES.USER.LINKEDIN_DISCONNECTED,
      },
    }),
  },
  warningTabs: {
    type: Array,
    default: () => ["Users"],
  },
  sequenceSteps: {
    type: Array,
    default: () => [],
  },
  position: {
    type: String,
    default: "start",
  },
  relativeToViewport: {
    type: Boolean,
    default: true,
  },
  hideHeader: {
    type: Boolean,
    default: false,
  },
  allowRemove: {
    type: [Boolean, Function],
    default: false,
  },
  showSelectAll: {
    type: Boolean,
    default: false,
  },
  selectAllUsersByDefault: {
    type: Boolean,
    default: false,
  },
  showClear: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "update:modelValue",
  "cta-click",
  "selection-change",
  "tab-change",
  "dropdown-open",
  "dropdown-close",
  "remove-option",
]);

const searchQuery = ref("");
const selectedValues = ref(
  props.tabs && props.tabs.length > 0
    ? props.tabs.map(() => [])
    : props.multiple
      ? [...props.modelValue]
      : props.modelValue && props.modelValue.length !== 0
        ? props.modelValue
        : null,
);

const getCurrentTabSelectedValues = computed(() => {
  const result =
    props.tabs && props.tabs.length > 0
      ? selectedValues.value[activeTabIndex.value] || []
      : selectedValues.value;

  return result;
});

const getAllSelectedValues = computed(() => {
  const result =
    props.tabs && props.tabs.length > 0
      ? selectedValues.value.flat()
      : selectedValues.value;

  return result;
});

const triggerElement = ref(null);
const measuredTriggerWidth = ref(null);
const dropdownRef = ref(null);
const activeTabIndex = ref(0);

const currentOptions = computed(() => {
  if (props.tabs && props.tabs.length > 0) {
    if (Array.isArray(props.options[0])) {
      return props.options[activeTabIndex.value] || [];
    }
    return props.options;
  }
  return props.options;
});

const currentSelectionMode = computed(() => {
  if (
    props.tabs &&
    props.tabs.length > 0 &&
    props.tabSelectionModes.length > 0
  ) {
    return props.tabSelectionModes[activeTabIndex.value] || "multi";
  }
  return props.multiple ? "multi" : "single";
});

const isCurrentTabMultiSelect = computed(() => {
  return currentSelectionMode.value === "multi";
});

const getCurrentSearchTitle = computed(() => {
  if (props.tabs && props.tabs.length > 0) {
    return `Choose ${props.tabs[activeTabIndex.value].toLowerCase()}`;
  }
  return props.searchTitle;
});

const hasEmailSteps = computed(() => {
  if (!props.sequenceSteps || props.sequenceSteps.length === 0) return false;
  return props.sequenceSteps.some(
    (step) => step.step_type === stepTypeConstants.EMAIL,
  );
});

const hasLinkedInSteps = computed(() => {
  if (!props.sequenceSteps || props.sequenceSteps.length === 0) return false;
  return props.sequenceSteps.some(
    (step) =>
      step.step_type && step.step_type.startsWith(stepTypeConstants.LINKEDIN),
  );
});

const getComputedStyles = computed(() => {
  const styles = {};

  if (props.customClass) {
    if (props.customClass.includes("w-full")) {
      styles.width = "100% !important";
    }
    if (props.customClass.includes("min-w-")) {
      const minWidthMatch = props.customClass.match(/min-w-\[([^\]]+)\]/);
      if (minWidthMatch) {
        styles.minWidth = minWidthMatch[1] + " !important";
      }
    }
  } else {
    styles.width = props.width;
    styles.minWidth = props.minWidth;
  }

  return styles;
});

const getDropdownContainerWidth = computed(() => {
  return "w-full";
});

const getDropdownStyles = computed(() => {
  const styles = {};

  if (props.matchTriggerWidth && measuredTriggerWidth.value) {
    styles.width = `${measuredTriggerWidth.value}px`;
    styles.minWidth = `${measuredTriggerWidth.value}px`;
  } else if (!props.matchTriggerWidth) {
    styles.width = props.dropdownWidth;
  }

  return styles;
});

const currentFilteredOptions = computed(() => {
  if (!searchQuery.value) {
    return currentOptions.value;
  }
  const query = searchQuery.value.toLowerCase();
  return currentOptions.value.filter((option) => {
    const label = getOptionLabel(option)?.toLowerCase() || "";
    const value = getOptionValue(option)?.toString()?.toLowerCase();
    return label.includes(query) || value.includes(query);
  });
});

const isAllSelected = computed(() => {
  if (!props.showSelectAll || !isCurrentTabMultiSelect.value) return false;
  
  const allOptions = currentFilteredOptions.value;
  const selectableOptions = allOptions.filter(opt => !opt.disabled);
  
  if (allOptions.length === 0) return false;
  
  if (selectableOptions.length === 0) {
    return allOptions.every(opt => isOptionSelected(getOptionValue(opt)));
  }
  
  return selectableOptions.every(opt => isOptionSelected(getOptionValue(opt)));
});

const isSelectAllDisabled = computed(() => {
  if (!props.showSelectAll || !isCurrentTabMultiSelect.value) return false;
  
  const allOptions = currentFilteredOptions.value;
  const selectableOptions = allOptions.filter(opt => !opt.disabled);
  
  return allOptions.length > 0 && selectableOptions.length === 0;
});

const isCurrentTabUsers = computed(() => {
  if (props.tabs && props.tabs.length > 0) {
    return props.tabs[activeTabIndex.value] === "Users";
  }
  return props.warningTabs.includes("Users") && props.placeholder?.toLowerCase().includes("user");
});

const isAllUsersSelected = computed(() => {
  if (!isCurrentTabUsers.value) return false;
  
  const allOptions = currentOptions.value;
  
  if (allOptions.length === 0) return false;
  
  const currentSelections = getCurrentTabSelectedValues.value;
  if (!Array.isArray(currentSelections) || currentSelections.length === 0) return false;
  
  // Check if all users (including disabled ones) are selected
  return allOptions.every(opt => currentSelections.includes(getOptionValue(opt)));
});

const handleSelectAll = () => {
  const allOptions = currentFilteredOptions.value;
  const selectableOptions = allOptions.filter(opt => !opt.disabled);
  const disabledOptions = allOptions.filter(opt => opt.disabled);
  
  const selectableValues = selectableOptions.map(opt => getOptionValue(opt));
  const disabledValues = disabledOptions.map(opt => getOptionValue(opt));
  
  if (props.tabs && props.tabs.length > 0) {
    // Tabbed mode
    let newTabSelections;
    if (isAllSelected.value) {
      const currentSelections = getCurrentTabSelectedValues.value;
      newTabSelections = currentSelections.filter(val => disabledValues.includes(val));
    } else {
      const currentSelections = getCurrentTabSelectedValues.value;
      const uniqueValues = new Set([...currentSelections, ...selectableValues]);
      newTabSelections = Array.from(uniqueValues);
    }
    
    selectedValues.value = selectedValues.value.map((tabSelections, index) => {
      if (index === activeTabIndex.value) {
        return newTabSelections;
      } else {
        return tabSelections;
      }
    });
    
    const flattenedSelections = selectedValues.value.flat();
    isInternalUpdate.value = true;
    
    emit("update:modelValue", flattenedSelections);
    emit("selection-change", {
      selectedValues: flattenedSelections,
      activeTab: {
        index: activeTabIndex.value,
        name: props.tabs[activeTabIndex.value],
      },
    });
  } else {
    // Non-tabbed mode
    let newSelections;
    if (isAllSelected.value) {
      const currentSelections = Array.isArray(selectedValues.value) ? selectedValues.value : [];
      newSelections = currentSelections.filter(val => disabledValues.includes(val));
    } else {
      const currentSelections = Array.isArray(selectedValues.value) ? selectedValues.value : [];
      const uniqueValues = new Set([...currentSelections, ...selectableValues]);
      newSelections = Array.from(uniqueValues);
    }
    
    selectedValues.value = newSelections;
    isInternalUpdate.value = true;
    
    emit("update:modelValue", newSelections);
    emit("selection-change", newSelections);
  }
};

const isOptionSelected = (value) => {
  if (props.tabs && props.tabs.length > 0) {
    const currentTabSelections = getCurrentTabSelectedValues.value;
    if (isCurrentTabMultiSelect.value) {
      return currentTabSelections.includes(value);
    } else {
      return (
        currentTabSelections.length > 0 && currentTabSelections[0] === value
      );
    }
  } else {
    if (props.multiple) {
      return selectedValues.value.includes(value);
    } else {
      return selectedValues.value === value;
    }
  }
};

const getOptionValue = (option) => {
  if (typeof option === "object" && option !== null) {
    return option.value;
  }
  return option;
};

const getOptionLabel = (option) => {
  if (typeof option === "object" && option !== null) {
    return option.label;
  }
  return option;
};

const getOptionByValue = (value) => {
  if (props.tabs && props.tabs.length > 0) {
    for (let tabIndex = 0; tabIndex < selectedValues.value.length; tabIndex++) {
      const tabValues = selectedValues.value[tabIndex];
      if (Array.isArray(tabValues) && tabValues.includes(value)) {
        const option = props.options[tabIndex]?.find(
          (opt) => getOptionValue(opt) === value,
        );
        if (option) return option;
      }
    }
    for (let i = 0; i < props.options.length; i++) {
      const option = props.options[i].find(
        (opt) => getOptionValue(opt) === value,
      );
      if (option) return option;
    }
  } else {
    return props.options.find((opt) => getOptionValue(opt) === value);
  }
  return null;
};

const getTabCount = (tabIndex) => {
  if (selectedValues.value && Array.isArray(selectedValues.value[tabIndex])) {
    return selectedValues.value[tabIndex].length;
  }
  return 0;
};

const processInitialTabSelection = () => {
  if (!props.tabs?.length || !props.modelValue?.length) {
    return;
  }

  const modelValueArray = normalizeToArray(props.modelValue);

  if (!hasValidOptions(props.options)) {
    return;
  }

  const newSelections = props.tabs.map(() => []);

  modelValueArray.forEach((value) => {
    for (let tabIndex = 0; tabIndex < props.options.length; tabIndex++) {
      const tabOptions = Array.isArray(props.options[tabIndex])
        ? props.options[tabIndex]
        : props.options;

      if (Array.isArray(tabOptions) && !tabOptions.length) {
        continue;
      }

      const foundOption = tabOptions.find(
        (option) => getOptionValue(option) === value,
      );

      if (foundOption) {
        if (props.tabSelectionModes[tabIndex] === "single") {
          newSelections[tabIndex] = [value];
        } else {
          newSelections[tabIndex].push(value);
        }
        break;
      }
    }
  });

  if (hasSelectionChanges(newSelections, selectedValues.value)) {
    selectedValues.value = newSelections;
  }
};

const getValueTabIndex = (value) => {
  if (props.tabs && props.tabs.length > 0 && selectedValues.value) {
    for (let tabIndex = 0; tabIndex < selectedValues.value.length; tabIndex++) {
      if (selectedValues.value[tabIndex].includes(value)) {
        return tabIndex;
      }
    }
  }
  return -1;
};

const getDisplayLabel = (value) => {
  if (!value && value !== 0) return "";

  if (props.tabs && props.tabs.length > 0 && Array.isArray(props.options[0])) {
    const tabIndex = getValueTabIndex(value);

    if (tabIndex !== -1 && props.options[tabIndex]) {
      const option = props.options[tabIndex].find(
        (opt) => getOptionValue(opt) === value,
      );
      if (option) {
        return getOptionLabel(option);
      }
    }

    for (let i = 0; i < props.options.length; i++) {
      const option = props.options[i].find(
        (opt) => getOptionValue(opt) === value,
      );
      if (option) {
        return getOptionLabel(option);
      }
    }
  } else {
    const option = currentOptions.value.find(
      (opt) => getOptionValue(opt) === value,
    );
    if (option) {
      return getOptionLabel(option);
    }
  }

  return value;
};

const setActiveTab = (index) => {
  activeTabIndex.value = index;
  searchQuery.value = "";
  
  const currentTabName = props.tabs[index];
  const tabOptions = Array.isArray(props.options[0])
    ? props.options[index] || []
    : props.options;
  
  if (tabOptions.length > 0) {
    let newTabSelections = [];
    
    if (currentTabName === "Users" && props.selectAllUsersByDefault) {
      newTabSelections = tabOptions.map(opt => getOptionValue(opt));
    }
    
    selectedValues.value = selectedValues.value.map((tabSelections, tabIndex) => {
      if (tabIndex === index) {
        return newTabSelections;
      }
      return [];
    });
    
    isInternalUpdate.value = true;
    const flattenedSelections = selectedValues.value.flat();
    emit("update:modelValue", flattenedSelections);
    emit("selection-change", {
      selectedValues: flattenedSelections,
      activeTab: {
        index: index,
        name: currentTabName,
      },
    });
  }
  
  emit("tab-change", {
    tabIndex: index,
    tabName: props.tabs[index],
  });
};

const getWarningMessage = (option) => {
  if (!props.showWarnings) return null;

  if (props.tabs && props.tabs.length > 0) {
    const currentTab = props.tabs[activeTabIndex.value];
    if (!props.warningTabs.includes(currentTab)) {
      return null;
    }
  }

  const optionData = typeof option === "object" ? option : null;
  if (!optionData) return null;

  const emailConnected = props.warningConfig.emailConnectedKey
    ? optionData[props.warningConfig.emailConnectedKey]
    : true;
  const linkedinConnected = props.warningConfig.linkedinConnectedKey
    ? optionData[props.warningConfig.linkedinConnectedKey]
    : true;

  const hasEmailStep = hasEmailSteps.value;
  const hasLinkedInStep = hasLinkedInSteps.value;
  const noSteps = !props.sequenceSteps || props.sequenceSteps.length === 0;

  if (noSteps) {
    if (!emailConnected && !linkedinConnected) {
      return props.warningConfig.messages.both;
    }
    if (emailConnected && !linkedinConnected) {
      return props.warningConfig.messages.emailOnly;
    }
    if (!emailConnected && linkedinConnected) {
      return props.warningConfig.messages.linkedinOnly;
    }
    return null;
  }

  if (hasEmailStep && !hasLinkedInStep) {
    if (!emailConnected) {
      return props.warningConfig.messages.emailDisconnected;
    }
    return null;
  }

  if (!hasEmailStep && hasLinkedInStep) {
    if (!linkedinConnected) {
      return props.warningConfig.messages.linkedinDisconnected;
    }
    return null;
  }

  if (hasEmailStep && hasLinkedInStep) {
    if (!emailConnected && !linkedinConnected) {
      return props.warningConfig.messages.both;
    }
    if (emailConnected && !linkedinConnected) {
      return props.warningConfig.messages.emailOnly;
    }
    if (!emailConnected && linkedinConnected) {
      return props.warningConfig.messages.linkedinOnly;
    }
    return null;
  }

  return null;
};

const resetSearch = () => {
  searchQuery.value = "";
};

const handleDropdownClose = () => {
  resetSearch();
  isDropdownOpen.value = false;
};

const handleDropdownTriggerClick = (data) => {
  isDropdownOpen.value = data.isOpen;
};

const toggleSelection = (value) => {
  if (props.tabs && props.tabs.length > 0) {
    const currentTabSelections = [...getCurrentTabSelectedValues.value];

    if (isCurrentTabMultiSelect.value) {
      const index = currentTabSelections.indexOf(value);
      if (index === -1) {
        currentTabSelections.push(value);
      } else {
        currentTabSelections.splice(index, 1);
      }
    } else {
      if (
        currentTabSelections.length > 0 &&
        currentTabSelections[0] === value
      ) {
        currentTabSelections.splice(0, 1);
      } else {
        currentTabSelections.splice(0, 1, value);
      }
    }

    selectedValues.value = selectedValues.value.map((tabSelections, index) => {
      if (index === activeTabIndex.value) {
        return currentTabSelections;
      } else {
        return tabSelections;
      }
    });

    const flattenedSelections = selectedValues.value.flat();
    isInternalUpdate.value = true;

    emit("update:modelValue", flattenedSelections);
    emit("selection-change", {
      selectedValues: flattenedSelections,
      activeTab: {
        index: activeTabIndex.value,
        name: props.tabs[activeTabIndex.value],
      },
    });

    if (!isCurrentTabMultiSelect.value && dropdownRef.value) {
      dropdownRef.value.closeDropdown();
    }
  } else {
    if (props.multiple) {
      const index = selectedValues.value.indexOf(value);
      if (index === -1) {
        selectedValues.value.push(value);
      } else {
        selectedValues.value.splice(index, 1);
      }
      isInternalUpdate.value = true;
      emit("update:modelValue", selectedValues.value);
      emit("selection-change", selectedValues.value);
    } else {
      selectedValues.value = value;
      isInternalUpdate.value = true;
      emit("update:modelValue", selectedValues.value);
      emit("selection-change", selectedValues.value);
      if (dropdownRef.value) {
        dropdownRef.value.closeDropdown();
      }
    }
  }
};

const removeItem = (value) => {
  if (props.tabs && props.tabs.length > 0) {
    let updated = false;
    selectedValues.value = selectedValues.value.map((tabSelections, index) => {
      const valueIndex = tabSelections.indexOf(value);
      if (valueIndex !== -1) {
        const newSelections = [...tabSelections];
        newSelections.splice(valueIndex, 1);
        updated = true;
        return newSelections;
      }
      return tabSelections;
    });

    if (updated) {
      const flattenedSelections = selectedValues.value.flat();
      isInternalUpdate.value = true;
      emit("update:modelValue", flattenedSelections);
      emit("selection-change", {
        selectedValues: flattenedSelections,
        activeTab: {
          index: activeTabIndex.value,
          name: props.tabs[activeTabIndex.value],
        },
      });
    }
  } else {
    if (props.multiple) {
      const index = selectedValues.value.indexOf(value);
      if (index !== -1) {
        selectedValues.value.splice(index, 1);
        isInternalUpdate.value = true;
        emit("update:modelValue", selectedValues.value);
        emit("selection-change", selectedValues.value);
      }
    } else {
      selectedValues.value = null;
      isInternalUpdate.value = true;
      emit("update:modelValue", selectedValues.value);
      emit("selection-change", selectedValues.value);
    }
  }
};

const handleCheckboxChange = (e, value) => {
  toggleSelection(value);
};

const handleCTA = () => {
  emit("cta-click");
  if (dropdownRef.value) {
    dropdownRef.value.closeDropdown();
  }
};

const handleRemoveOption = (option) => {
  emit("remove-option", {
    option: option,
    value: getOptionValue(option),
    label: getOptionLabel(option),
  });
  if (dropdownRef.value) {
    dropdownRef.value.closeDropdown();
  }
};

const shouldAllowRemove = (option) => {
  if (typeof props.allowRemove === "function") {
    const value = getOptionValue(option);
    return props.allowRemove(option, value);
  }
  return !!props.allowRemove;
};

const handleTriggerIconClick = () => {
  if (!props.showClear) return;
  if (!props.multiple) {
    if (props.tabs && props.tabs.length > 0) {
      const currentSelected = getCurrentTabSelectedValues.value;
      if (currentSelected.length > 0) {
        removeItem(currentSelected[0]);
      }
    } else if (selectedValues.value) {
      removeItem(selectedValues.value);
    }
  }
};

const measureTriggerWidth = () => {
  if (props.matchTriggerWidth && triggerElement.value) {
    const rect = triggerElement.value.getBoundingClientRect();
    measuredTriggerWidth.value = rect.width;
  }
};

const isInternalUpdate = ref(false);

const isDropdownOpen = ref(false);

watch(
  () => dropdownRef.value?.isOpen,
  (newValue) => {
    if (newValue !== undefined) {
      isDropdownOpen.value = newValue;
    }
  },
);

watch(isDropdownOpen, (newValue) => {
  if (newValue) {
    emit("dropdown-open");
  } else {
    emit("dropdown-close");
  }
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (isInternalUpdate.value) {
      isInternalUpdate.value = false;
      return;
    }

    if (props.tabs && props.tabs.length > 0) {
      const newSelections = props.tabs.map(() => []);

      if (Array.isArray(newValue) && newValue.length > 0) {
        newValue.forEach((value) => {
          for (let tabIndex = 0; tabIndex < props.options.length; tabIndex++) {
            const tabOptions = Array.isArray(props.options[tabIndex])
              ? props.options[tabIndex]
              : props.options;

            const foundOption = tabOptions.find(
              (option) => getOptionValue(option) === value,
            );
            if (foundOption) {
              if (props.tabSelectionModes[tabIndex] === "single") {
                newSelections[tabIndex] = [value];
              } else {
                newSelections[tabIndex].push(value);
              }
              break;
            }
          }
        });
      }

      selectedValues.value = newSelections;
    } else {
      if (props.multiple) {
        selectedValues.value = [...newValue];
      } else {
        selectedValues.value =
          (Array.isArray(newValue) && newValue.length === 0) || !newValue
            ? null
            : newValue;
      }
    }
  },
  { deep: true },
);

watch(
  [
    () => (props.multiple ? selectedValues.value.length : selectedValues.value),
    () => props.width,
    () => props.customClass,
  ],
  () => {
    if (props.matchTriggerWidth) {
      nextTick(() => {
        measureTriggerWidth();
      });
    }
  },
  { deep: true },
);

const initializeDefaultSelection = () => {
  if (!props.selectAllUsersByDefault) {
    return;
  }

  if (props.tabs && props.tabs.length > 0) {
    const usersTabIndex = props.tabs.findIndex(tab => tab === "Users");
    
    if (usersTabIndex === -1) return;
    
    const usersTabOptions = Array.isArray(props.options[0])
      ? props.options[usersTabIndex] || []
      : props.options;
    
    if (usersTabOptions.length === 0) return;

    const allUserValues = usersTabOptions.map(opt => getOptionValue(opt));
    
    if (allUserValues.length === 0) return;
    
    selectedValues.value = selectedValues.value.map((tabSelections, index) => {
      if (index === usersTabIndex) {
        return allUserValues;
      }
      return tabSelections;
    });
    
    isInternalUpdate.value = true;
    emit("update:modelValue", selectedValues.value.flat());
    emit("selection-change", {
      selectedValues: selectedValues.value.flat(),
      activeTab: {
        index: activeTabIndex.value,
        name: props.tabs[activeTabIndex.value],
      },
    });
  }
};

onMounted(() => {
  processInitialTabSelection();
  
  nextTick(() => {
    initializeDefaultSelection();
  });

  if (props.matchTriggerWidth) {
    nextTick(() => {
      measureTriggerWidth();

      if (triggerElement.value && typeof ResizeObserver !== "undefined") {
        const resizeObserver = new ResizeObserver(() => {
          measureTriggerWidth();
        });
        resizeObserver.observe(triggerElement.value);
      }
    });
  }
});

watch(
  [() => props.tabs, () => props.options],
  ([newTabs, newOptions]) => {
    if (hasValidOptions(newOptions)) {
      processInitialTabSelection();
      nextTick(() => {
        initializeDefaultSelection();
      });
    }
  },
  { deep: true },
);

defineExpose({
  resetSearch,
  setActiveTab,
  getActiveTab: () => activeTabIndex.value,
});
</script>

<style scoped>
.dropdown_item:hover {
  background-color: var(--white-opacity-10);
}

.dropdown_item_selected {
  background-color: var(--white-opacity-10);
}

.input_medium.input_error {
  border: 1px solid var(--error-color) !important;
}

.trigger_icon_container {
  position: relative;
}

.trigger_clear_icon {
  position: absolute;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.trigger_arrow_icon {
  position: absolute;
  opacity: 1;
  transition: opacity 0.2s ease;
}

.input_medium:hover .trigger_clear_icon {
  opacity: 1;
}

.input_medium:hover .trigger_arrow_icon {
  opacity: 1;
}

.input_medium:hover
  .trigger_icon_container
  .trigger_clear_icon
  ~ .trigger_arrow_icon {
  opacity: 0;
}

.input_medium:not(:hover) .trigger_clear_icon {
  opacity: 0;
}

.input_medium:not(:hover) .trigger_arrow_icon {
  opacity: 1;
}

.border_bottom_active {
  border-bottom: 2px solid var(--primary-color);
}

.list_category {
  color: var(--secondary-color);
  line-height: 17px;
  white-space: nowrap;
}

.list_category:hover {
  background-color: var(--white-opacity-6);
}

.list_category.active {
  background-color: var(--white-opacity-6);
  color: var(--text-color);
}

.warning_icon_container {
  border-radius: 24px;
  background: var(--warning-icon-bg);
  padding: 4px;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.tab_count_badge {
  border-radius: 24px;
  background: var(--white-opacity-12);
  display: flex;
  min-width: 16px;
  padding: 0 4px;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  font-weight: 500;
  line-height: 16px;
  color: var(--secondary-color);
}
</style>
